import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Collapse } from 'reactstrap';
import PropTypes from 'prop-types';
import BaseTable, { Column, AutoResizer } from 'react-base-table';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@material-ui/icons/Search';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';
import TextField from '@material-ui/core/TextField';
import {
  createMuiTheme,
  makeStyles,
  MuiThemeProvider,
} from '@material-ui/core/styles';
import { Tooltip } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import ActionsColumn from './ActionsColumn';
import CheckboxColumn from './CheckboxColumn';
import { escapeRegExp } from '../../utils/smartutils';

const theme = createMuiTheme({
  palette: {
    primary: {
      main: '#9d5fbe',
    },
    secondary: {
      main: '#9d5fbe',
    },
  },
  overrides: {
    MuiToolbar: {
      gutters: {
        paddingLeft: '0px !important',
      },
    },
    MuiTableCell: {
      head: {
        color: '#fff',
        background: '#9d5fbe !important',
      },
    },
    MuiTableSortLabel: {
      root: {
        color: '#fff',
        '&:hover': {
          color: '#debeed',
        },
      },
      active: {
        color: '#debeed !important',
      },
      icon: {
        color: '#debeed !important',
      },
    },
    MuiSvgIcon: {
      root: {
        color: '#6A2B8B',
      },
    },
    MuiCollapse: {
      container: {
        width: '100%',
      },
    },
    MuiTypography: {
      root: {
        color: '#6A2B8B',
        paddingLeft: '0px !important',
      },
      h6: {
        color: '#9d5fbe !important',
        fontSize: '14px',
      },
    },
    MuiIconButton: {
      root: {
        maxHeight: '3rem',
      },
    },
    MuiInputBase: {
      input: {
        color: '#6A2B8B',
      },
    },
    MuiSelect: {
      icon: {
        color: '#6A2B8B',
      },
    },
  },
});

const useStyles = makeStyles(() => ({
  rowCell: {
    '&:focus-within': {
      outline: '0 !important',
    },
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    height: '3.25rem',
  },
  root: {
    padding: theme.spacing(0.5, 0.5, 0),
    justifyContent: 'space-between',
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  textField: {
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    margin: theme.spacing(1, 0.5, 1.5),
    '& .MuiSvgIcon-root': {
      marginRight: theme.spacing(0.5),
    },
    '& .MuiInput-underline:before': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
}));

const Toolbar = ({
  t,
  value,
  onChange,
  isFiltering,
  title,
  clearSearch,
  filterBuildFunction,
  onFilterClick,
  customActionsToolbar,
  selection,
}) => {
  useState(false);
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <div className="top-bar">{title}</div>
      <div className="d-flex">
        <TextField
          variant="standard"
          value={value}
          onChange={onChange}
          placeholder={t('general.tables.search_placeholder')}
          className={classes.textField}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" />,
            endAdornment: (
              <IconButton
                title="clear"
                aria-label="Clear"
                size="small"
                style={{ visibility: value ? 'visible' : 'hidden' }}
                onClick={clearSearch}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            ),
          }}
        />
        {customActionsToolbar.length ? (
          <>
            {customActionsToolbar.map((action) => {
              if (selection.length && action.toolbarOnSelect) {
                return (
                  <Tooltip key={action.name} title={action.tooltip}>
                    <IconButton
                      disabled={false}
                      onClick={(evt) => action.callback(evt, selection)}
                    >
                      {action.icon}
                    </IconButton>
                  </Tooltip>
                );
              }
              if (!selection.length && !action.toolbarOnSelect) {
                return (
                  <Tooltip key={action.name} title={action.tooltip}>
                    <IconButton
                      disabled={false}
                      onClick={(evt) => action.callback(evt)}
                    >
                      {action.icon}
                    </IconButton>
                  </Tooltip>
                );
              }
              return null;
            })}
          </>
        ) : null}
        {onFilterClick && (
          <Tooltip title={t('general.tables.filter-list')}>
            <IconButton onClick={onFilterClick}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>
      <Collapse isOpen={isFiltering} appear>
        <div> {filterBuildFunction ? filterBuildFunction() : null}</div>
      </Collapse>
    </div>
  );
};

Toolbar.propTypes = {
  t: PropTypes.func.isRequired,
  clearSearch: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  title: PropTypes.string,
  isFiltering: PropTypes.bool,
  onFilterClick: PropTypes.func,
  filterBuildFunction: PropTypes.func,
  selection: PropTypes.arrayOf(PropTypes.number),
  customActionsToolbar: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      icon: PropTypes.element.isRequired,
      tooltip: PropTypes.string.isRequired,
      callback: PropTypes.func.isRequired,
      toolbarOnSelect: PropTypes.bool,
    })
  ),
};

Toolbar.defaultProps = {
  isFiltering: false,
  value: '',
  title: '',
  filterBuildFunction: null,
  onFilterClick: null,
  customActionsToolbar: [],
  selection: [],
};

const SmartBaseTable = forwardRef((props, ref) => {
  const {
    columns,
    data,
    onSelectionChange,
    title,
    onEditCell,
    customActionsCell,
    actionsDisabledByConditional,
    customActionsToolbar,
    customFooterBar,
    heightBelow,
    footerHeight,
  } = props;
  const { t } = useTranslation('common');
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchText, setSearchText] = React.useState('');
  const [rows, setRows] = React.useState(data);

  const handleSelect = (row) => {
    if (selectedRows.includes(row.id)) {
      setSelectedRows(selectedRows.filter((id) => id !== row.id));
    } else {
      setSelectedRows([...selectedRows, row.id]);
    }
  };

  const handleSelectAll = (checked, allData) => {
    if (checked) {
      setSelectedRows(allData.map((it) => it.id));
    } else {
      setSelectedRows([]);
    }
  };

  const getWidth = () => {
    if (customActionsCell.length) {
      return 80 * customActionsCell.length;
    }
    return 100;
  };

  const getWidthColumn = (column) => {
    if (column.cellRenderer) {
      return 200;
    }
    if (column.minWidth) {
      return column.minWidth;
    }
    return 100;
  };

  const getColumns = () => {
    return columns.map((column, columnIndex) => (
      <Column
        key={`col${columnIndex + columnIndex}`}
        title={column.title}
        dataKey={column.field}
        sortable
        width={getWidthColumn(column)}
        {...column}
      />
    ));
  };

  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
    const filteredRows = data.filter((row) => {
      return Object.keys(row).some((field) => {
        return searchRegex.test(JSON.stringify(row[field]));
      });
    });
    setRows(filteredRows);
  };

  const changeRows = (array) => {
    setRows(array);
  };

  useImperativeHandle(ref, () => ({
    handleSelectAll,
  }));

  useEffect(() => {
    changeRows(data || []);
  }, [data]);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedRows);
    }
  }, [selectedRows]);

  return (
    <MuiThemeProvider theme={theme}>
      <div
        className="smart-base-table"
        style={{ height: window.innerHeight - heightBelow }}
      >
        <AutoResizer>
          {({ width }) => (
            <div className="d-flex flex-column">
              <div style={{ width }}>
                <Toolbar
                  title={title}
                  value={searchText}
                  onChange={(event) => requestSearch(event.target.value)}
                  clearSearch={() => requestSearch('')}
                  customActionsToolbar={customActionsToolbar}
                  selection={selectedRows}
                  t={t}
                  {...props}
                />
              </div>
              <BaseTable
                headerClassName="base-table-header"
                fixed
                footerHeight={footerHeight}
                width={width}
                height={window.innerHeight - heightBelow}
                data={rows}
                emptyRenderer={
                  <div className="base-table-empty">
                    {t('general.tables.no-data-to-display')}
                  </div>
                }
                footerRenderer={
                  customFooterBar && (
                    <div className="base-table-bottom-bar">
                      {customFooterBar}
                    </div>
                  )
                }
              >
                {onSelectionChange && (
                  <CheckboxColumn
                    key="checkbox"
                    dataKey="id"
                    flexGrow={0}
                    width={70}
                    selectedRows={selectedRows}
                    onSelect={handleSelect}
                    onSelectAll={handleSelectAll}
                  />
                )}
                {getColumns()}
                <ActionsColumn
                  t={t}
                  title={t('general.tables.actions')}
                  key="actions"
                  dataKey="id"
                  flexGrow={5}
                  flexShrink={0}
                  width={getWidth()}
                  onEditCell={onEditCell}
                  customActionsCell={customActionsCell}
                  actionsDisabledByConditional={actionsDisabledByConditional}
                />
              </BaseTable>
            </div>
          )}
        </AutoResizer>
      </div>
    </MuiThemeProvider>
  );
});

SmartBaseTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape).isRequired,
  onEditCell: PropTypes.func,
  onSelectionChange: PropTypes.func,
  title: PropTypes.string,
  customActionsCell: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      icon: PropTypes.element.isRequired,
      tooltip: PropTypes.string.isRequired,
      callback: PropTypes.func.isRequired,
    })
  ),
  customActionsToolbar: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      icon: PropTypes.element.isRequired,
      tooltip: PropTypes.string.isRequired,
      callback: PropTypes.func.isRequired,
      toolbarOnSelect: PropTypes.bool,
    })
  ),
  isFiltering: PropTypes.bool,
  onFilterClick: PropTypes.func,
  onAddClick: PropTypes.func,
  onRefreshClick: PropTypes.func,
  filterBuildFunction: PropTypes.func,
  actionsDisabledByConditional: PropTypes.func,
  customFooterBar: PropTypes.element,
  heightBelow: PropTypes.number,
  footerHeight: PropTypes.number,
};

SmartBaseTable.defaultProps = {
  onEditCell: null,
  onSelectionChange: null,
  customActionsCell: [],
  customActionsToolbar: [],
  title: '',
  isFiltering: false,
  filterBuildFunction: null,
  onFilterClick: null,
  onAddClick: null,
  onRefreshClick: null,
  actionsDisabledByConditional: null,
  customFooterBar: false,
  heightBelow: 68,
  footerHeight: 0,
};

export default SmartBaseTable;
